import React from 'react';
import { observer } from 'mobx-react';
import { injectable } from 'inversify';
import { NeoModel, EnumHelper, Data, Model, List } from '@singularsystems/neo-core';
import { Neo, Views } from '@singularsystems/neo-react';
import { AppService, Types } from '../CommonParticipantTypes';
import Beneficiary from '../Models/Beneficiary';
import AddressComponent from './AddressComponent';
import { RelationshipType } from '../Models/Enums/RelationshipType';
import { NotificationDuration } from '../../App/Models/Enums/NotificationDuration';

interface IBeneficiaryModalProps<TIdType, TCountry> {
    viewModel: BeneficiaryModalVM;
    idTypes: Data.IAsyncDataSource<TIdType[]>;
    countries: Data.IAsyncDataSource<TCountry[]>;
}

interface ISuspendedEdit {
    editingBeneficiary: Beneficiary;
    originalRecord: Beneficiary | null;
    newRecordList: List<Beneficiary> | null;
}

@injectable()
@NeoModel
export class BeneficiaryModalVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications)) {
        super(taskRunner);
    }

    public saveCallback?: () => void;

    public editingBeneficiary: Beneficiary | null = null;

    private originalRecord: Beneficiary | null = null;

    private newRecordList: List<Beneficiary> | null = null;

    private newGuardianTarget: Beneficiary | null = null;

    private suspended: ISuspendedEdit | null = null;

    public addressCardExpanded = new Model.ObservableValue(false);

    public get isGuardianStep(): boolean {
        return this.suspended !== null;
    }

    public get suspendedMinorName(): string {
        return this.suspended ? this.suspended.editingBeneficiary.firstName : "";
    }

    private showRecord(beneficiary: Beneficiary, originalRecord: Beneficiary | null, newRecordList: List<Beneficiary> | null, newGuardianTarget: Beneficiary | null = null) {
        beneficiary.validator.forceShowErrors = false;
        beneficiary.postalAddress.validateAddress = true;
        this.addressCardExpanded.value = false;
        this.editingBeneficiary = beneficiary;
        this.originalRecord = originalRecord;
        this.newRecordList = newRecordList;
        this.newGuardianTarget = newGuardianTarget;
    }

    public showAdd(list: List<Beneficiary>, participantId: string | null, sharePercentage: number) {
        this.suspended = null;
        const beneficiary = new Beneficiary();
        beneficiary.participantId = participantId;
        beneficiary.sharePercentage = sharePercentage;
        beneficiary.isSelfDirty = false;
        beneficiary.postalAddress.isSelfDirty = false;
        this.showRecord(beneficiary, null, list);
    }

    public showEdit(beneficiary: Beneficiary, list: List<Beneficiary>) {
        this.suspended = null;
        this.showRecord(beneficiary.cloneWithIdentity(), beneficiary, list);
    }

    public addGuardian() {
        if (!this.editingBeneficiary) return;

        this.suspended = { editingBeneficiary: this.editingBeneficiary, originalRecord: this.originalRecord, newRecordList: this.newRecordList };

        const guardian = new Beneficiary();
        guardian.isGuardian = true;
        guardian.participantId = this.suspended.editingBeneficiary.participantId;
        guardian.isSelfDirty = false;
        guardian.postalAddress.isSelfDirty = false;

        this.showRecord(guardian, null, null, this.suspended.editingBeneficiary);
    }

    public showEditGuardian() {
        const minor = this.editingBeneficiary;
        if (!minor?.guardian) return;

        this.suspended = { editingBeneficiary: minor, originalRecord: this.originalRecord, newRecordList: this.newRecordList };
        this.showRecord(minor.guardian.cloneWithIdentity(), minor.guardian, null);
    }

    public editGuardian(minor: Beneficiary) {
        if (!minor.guardian) return;

        this.suspended = null;
        this.showRecord(minor.guardian.cloneWithIdentity(), minor.guardian, null);
    }

    public removeGuardian() {
        if (this.editingBeneficiary) {
            this.editingBeneficiary.guardian = null;
            this.editingBeneficiary.isExpanded = false;
        }
    }

    public save() {
        if (!this.editingBeneficiary) return;

        this.commitEditingBeneficiary();

        if (this.suspended) {
            const { editingBeneficiary, originalRecord, newRecordList } = this.suspended;
            this.suspended = null;
            this.showRecord(editingBeneficiary, originalRecord, newRecordList);
            return;
        }

        this.editingBeneficiary = null;
        this.originalRecord = null;
        this.newRecordList = null;
        this.newGuardianTarget = null;
        this.saveCallback?.();
    }

    private commitEditingBeneficiary() {
        if (!this.editingBeneficiary) return;

        if (this.originalRecord) {
            this.originalRecord.update(this.editingBeneficiary.toJSObject());
        } else if (this.newGuardianTarget) {
            this.newGuardianTarget.guardian = this.editingBeneficiary;
        } else {
            this.newRecordList?.push(this.editingBeneficiary);
        }
    }

    public onInvalid() {
        this.notifications.addWarning(
            "Cannot Save",
            "Please fix the highlighted fields before continuing.",
            NotificationDuration.Standard
        );

        if (this.editingBeneficiary && !this.editingBeneficiary.postalAddress.isValid) {
            this.addressCardExpanded.value = true;
        }
    }
}

@observer
export default class BeneficiaryModal<TIdType, TCountry> extends React.Component<IBeneficiaryModalProps<TIdType, TCountry>> {
    public render() {
        const viewModel = this.props.viewModel;
        const editing = viewModel.editingBeneficiary;

        if (!editing) return null;

        const isGuardianStep = viewModel.isGuardianStep;

        return (
            <Neo.Modal
                title={
                    isGuardianStep
                        ? "Add Guardian (step 2 of 2)"
                        : editing.isGuardian
                            ? (editing.isNew ? "Add Guardian" : "Edit Guardian")
                            : (editing.isNew ? "Add Beneficiary" : "Edit Beneficiary")
                }
                bind={viewModel.meta.editingBeneficiary}
                size="lg"
                acceptButton={{
                    text: isGuardianStep ? "Continue" : "Save",
                    onClick: () => viewModel.save()
                }}
                formProps={{
                    onInvalid: () => viewModel.onInvalid()
                }}
            >
                {isGuardianStep && (
                    <Neo.Alert variant="info">
                        {viewModel.suspendedMinorName} is under 18 — add their guardian's details below.
                        You'll return to {viewModel.suspendedMinorName}'s details to finish.
                    </Neo.Alert>
                )}
                <Neo.Card title={<strong>Personal Information</strong>}>
                    <Neo.FormGroupInline label="First Name" bind={editing.meta.firstName} />
                    <Neo.FormGroupInline label="Last Name" bind={editing.meta.lastName} />
                    <Neo.FormGroupInline label="Date of Birth" bind={editing.meta.dateOfBirth} />

                    {!isGuardianStep && editing.isMinor && !editing.guardian && (
                        <Neo.Alert variant="warning">
                            This beneficiary is a minor and requires a guardian.{" "}
                            <Neo.Button variant="primary" icon="plus-circle" onClick={() => viewModel.addGuardian()}>
                                Add Guardian
                            </Neo.Button>
                        </Neo.Alert>
                    )}

                    <Neo.FormGroupInline label="ID Type" bind={editing.meta.idTypeId} select={{ itemSource: this.props.idTypes }} />
                    <Neo.FormGroupInline label="ID/Passport Number" bind={editing.meta.idNumber} />
                    <Neo.FormGroupInline label="Mobile Number" bind={editing.meta.mobileNumber} input={{ type: "tel" }} />
                    <Neo.FormGroupInline label="Email Address" bind={editing.meta.email} input={{ type: "email" }} />
                    <Neo.FormGroupInline
                        label={editing.isGuardian ? "Relationship to Beneficiary" : "Relationship"}
                        bind={editing.meta.relationship}
                        select={{ items: EnumHelper.asList(RelationshipType) }}
                    />
                </Neo.Card>

                <Neo.Card
                    title={<strong>Address</strong>}
                    isExpanded={viewModel.addressCardExpanded}
                    headerElements={<span className="text-muted small">{summariseAddress(editing.postalAddress)}</span>}
                >
                    <AddressComponent address={editing.postalAddress} showValidate={false} countries={this.props.countries} />
                </Neo.Card>

                {!isGuardianStep && editing.guardian && (
                    <div className="d-flex align-items-center mt-3">
                        <span className="me-2">Guardian: <strong>{editing.guardian.firstName} {editing.guardian.lastName}</strong></span>
                        <Neo.Button className="me-2" size="sm" variant="info" icon="edit" onClick={() => viewModel.showEditGuardian()}>
                            Edit
                        </Neo.Button>
                        <Neo.Button size="sm" variant="danger" icon="trash" onClick={() => viewModel.removeGuardian()}>
                            Remove
                        </Neo.Button>
                    </div>
                )}
            </Neo.Modal>
        );
    }
}

function summariseAddress(address: { street: string; city: string }): string {
    return [address.street, address.city].filter(Boolean).join(", ");
}
